import React, { useState, useCallback, useEffect } from 'react';
import { strings } from 'src/localization';
import * as css from './ContactDealer.module.scss';
import { Customer, SpecData, Address, ascPageviewInput, ascCtaInteractionInput, VehicleRec } from 'src/models';
import { BlankDeptSpec, BlankAddress } from 'src/models/Specs/TestSpecs';
import ContactSection from './ContactSection';
import DepartmentTile from './DepartmentTile';
import { convertTimeZeros } from 'src/helpers/TimeDisplayHelper/TimeDisplayHelper';
import classNames from 'classnames';
import _ from 'lodash';
import { ScrollBar } from '@framework-controls/core';
import { ascCtaInteraction, ascPageview } from 'src/helpers/EventHelper/EventHelper';

export interface IPropState {
    custInfo?: Customer,
    identityVerified?: boolean,
    specInfo?: SpecData,
    theme: string,
    address?: Address,
    locale: string,
    vehicles: VehicleRec[],
    selectedVehicle?: string,
}

const ContactDealer = (props: IPropState) => {
    const { custInfo, specInfo } = { ...props };
    const [header, setHeader] = useState('');
    const [deptSpecs, setDeptSpecs] = useState(BlankDeptSpec);

    useEffect(() => {
        if (deptSpecs === BlankDeptSpec && specInfo) {
            const tileHeaders = {
                service: specInfo?.svcSpecs?.contactWebNme ? specInfo.svcSpecs.contactWebNme : strings.Service,
                body: specInfo?.bdySpecs?.contactWebNme ? specInfo.bdySpecs.contactWebNme : strings.BdyShop,
                oss: specInfo?.ossSpecs?.contactWebNme ? specInfo.ossSpecs.contactWebNme : strings.Express,
            };
            const specs = [
                { dept: specInfo.svcSpecs,
                    header: tileHeaders.service },
                { dept: specInfo.bdySpecs,
                    header: tileHeaders.body },
                { dept: specInfo.ossSpecs,
                    header: tileHeaders.oss },

            ];
            for (let i = 0; i < specs.length; i++) {
                if (specs[i].dept) {
                    setDeptSpecs(specs[i].dept!);
                    setHeader('Contact ' + specs[i].header);
                    break;
                }
            }
            // setDeptSpecs(specInfo.svcSpecs!);
            // setHeader('Contact ' + strings.Service);
        }
    }, [setDeptSpecs, specInfo, deptSpecs]);

    const daysOfWeek: string[] = [
        strings.Monday,
        strings.Tuesday,
        strings.Wednesday,
        strings.Thursday,
        strings.Friday,
        strings.Saturday,
        strings.Sunday,
    ];

    const hoursLines: string[] = [];

    let deptAddress = [''];
    let deptPhone = '';

    const strNum = deptSpecs.contactAddr.contactStrNum ?? '';
    const contactStreet = deptSpecs.contactAddr.contactStreet ?? '';
    const contactCity = deptSpecs.contactAddr.contactCity ?? '';
    const contactState = deptSpecs.contactAddr.contactState ?? '';
    const contactZip = deptSpecs.contactAddr.contactZip ?? '';

    if (!_.isEqual(deptSpecs.contactAddr, BlankAddress)) {
        deptAddress = [
            strNum + ' ' + contactStreet,
            contactCity + ', ' + contactState + ' ' + contactZip];
    }

    if (deptSpecs.contactPhnNum) {
        const match = deptSpecs.contactPhnNum.match(/^(1|)?(\d{3})?(\d{3})(\d{4})$/);

        if (match) {
            const intlCode = match[1] ? match[1] + '-' : '';
            const areaCode = match[2] ? match[2] + '-' : '';
            deptPhone = `${intlCode}${areaCode}${match[3]}-${match[4]}`;
        }
    }

    for (let i = 0; i < 7; i++) {
        if (!deptSpecs.cntctHrsOpen1[i] && !deptSpecs.cntctHrsOpen2[i] && !deptSpecs.cntctHrsOpen3[i]) {
            hoursLines.push(daysOfWeek[i] +
                ' ' + strings.Closed);
        }
        if (deptSpecs.cntctHrsOpen1[i]) {
            hoursLines.push(daysOfWeek[i] +
            ' ' + convertTimeZeros(deptSpecs.cntctHrsOpen1[i], true, true) +
                ' - ' + convertTimeZeros(deptSpecs.cntctHrsClose1[i], true, true));
        }
        if (deptSpecs.cntctHrsOpen2[i]) {
            hoursLines[i] += ' | ' + convertTimeZeros(deptSpecs.cntctHrsOpen2[i], true, true) +
                ' - ' + convertTimeZeros(deptSpecs.cntctHrsClose2[i], true, true);
        }
        if (deptSpecs.cntctHrsOpen3[i]) {
            hoursLines[i] += ' | ' + convertTimeZeros(deptSpecs.cntctHrsOpen3[i], true, true) +
                ' - ' + convertTimeZeros(deptSpecs.cntctHrsClose3[i], true, true);
        }
    }

    const setDepartmentSpecs = useCallback((header: string) => {
        setHeader(header);
        let elementText = header;
        const serviceText = specInfo?.svcSpecs?.contactWebNme ? specInfo.svcSpecs.contactWebNme : strings.Service;
        const bodyText = specInfo?.bdySpecs?.contactWebNme ? specInfo.bdySpecs.contactWebNme : strings.BdyShop;
        const ossText = specInfo?.ossSpecs?.contactWebNme ? specInfo.ossSpecs.contactWebNme : strings.Express;
        switch (header) {
            case ('Contact ' + strings.Service):
                setDeptSpecs(specInfo?.svcSpecs!);
                elementText = serviceText;
                break;
            case ('Contact ' + specInfo?.svcSpecs?.contactWebNme):
                setDeptSpecs(specInfo?.svcSpecs!);
                elementText = serviceText;
                break;
            case ('Contact ' + strings.BdyShop):
                setDeptSpecs(specInfo?.bdySpecs!);
                elementText = bodyText;
                break;
            case ('Contact ' + specInfo?.bdySpecs?.contactWebNme):
                setDeptSpecs(specInfo?.bdySpecs!);
                elementText = bodyText;
                break;
            case ('Contact ' + strings.Express):
                setDeptSpecs(specInfo?.ossSpecs!);
                elementText = ossText;
                break;
            case ('Contact ' + specInfo?.ossSpecs?.contactWebNme):
                setDeptSpecs(specInfo?.ossSpecs!);
                elementText = ossText;
                break;
            default:
                setDeptSpecs(specInfo?.svcSpecs!);
                elementText = serviceText;
                break;
        }
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'contact',
            elementText: elementText,
            elementColor: '',
            elementOrder: '',
            elementType: 'header',
            elementSubType: 'nav_tab',
            eventAction: 'click',
            eventActionResult: 'next_step',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, '', props.vehicles, props.selectedVehicle);
    }, [specInfo, props.vehicles, props.selectedVehicle]);

    const sections = (
        <div className={css.sections}>
            {deptSpecs.contactPhnNum &&
                <ContactSection
                    header={strings.PhoneHeader}
                    lines={[deptPhone]}
                    telephone
                    ext= {deptSpecs.contactExt}
                    vehicles={props.vehicles}
                    selectedVehicle={props.selectedVehicle}
                    theme={props.theme}
                />
            }

            {deptSpecs.contactEmail &&
                <ContactSection
                    header={strings.EmailHeader}
                    lines={[deptSpecs.contactEmail]}
                    email
                    vehicles={props.vehicles}
                    selectedVehicle={props.selectedVehicle}
                    theme={props.theme}
                />
            }

            {deptSpecs.contactAddr.contactStrNum &&
                <ContactSection
                    header={strings.AddressHeader}
                    lines={deptAddress}
                    vehicles={props.vehicles}
                    selectedVehicle={props.selectedVehicle}
                    theme={props.theme}
                />
            }

            {deptSpecs.cntctHrsOpen1 &&
                <ContactSection
                    header={strings.Hours}
                    lines={hoursLines}
                    times
                    vehicles={props.vehicles}
                    selectedVehicle={props.selectedVehicle}
                    theme={props.theme}
                />
            }

            {deptSpecs.comingClosures &&
                <ContactSection
                    header={strings.UpcomingClosures}
                    lines={deptSpecs.comingClosures}
                    closures
                    locale={props.locale}
                    vehicles={props.vehicles}
                    selectedVehicle={props.selectedVehicle}
                    theme={props.theme}
                />
            }

            {deptSpecs.addtnlInfo &&
                <ContactSection
                    header={strings.AdditionalInfo}
                    lines={[deptSpecs.addtnlInfo]}
                    paragraph
                    vehicles={props.vehicles}
                    selectedVehicle={props.selectedVehicle}
                    theme={props.theme}
                />
            }
        </div>
    );

    const tileLabels = {
        service: specInfo?.svcSpecs?.contactWebNme ? specInfo.svcSpecs.contactWebNme : strings.Service,
        body: specInfo?.bdySpecs?.contactWebNme ? specInfo.bdySpecs.contactWebNme : strings.BdyShop,
        oss: specInfo?.ossSpecs?.contactWebNme ? specInfo.ossSpecs.contactWebNme : strings.Express,
    };

    const contactDealerCSS = classNames(
        css.contactDealer,
        {
            [css.contactDealerBMW]: props.theme==='bmw',
        },
    );
    const contactDeptHeader = classNames(
        css.contactDeptHeader,
        {
            [css.contactDeptHeaderLexus]: props.theme === 'lexus',
            [css.contactDeptHeaderMazda]: props.theme === 'mazda',
            [css.contactDeptHeaderGenesis]: props.theme === 'genesis',
        },
    );
    const sectionArea = classNames(
        css.sectionArea,
        {
            [css.sectionAreaLexus]: props.theme === 'lexus',
        },
    );

    const nameClasses = classNames(
        css.name,
        {
            [css.mazdaName]: props.theme === 'mazda',
            [css.genesisName]: props.theme === 'genesis',
            [css.fontSize178Rem]: props.theme === 'mercedesBenz',
        },
    );

    useEffect(() => {
        const ascPageViewInp: ascPageviewInput = {
            pageType: 'contact',
            errorCode: '',
            itemResults: '',
        };
        ascPageview(ascPageViewInp, props.vehicles, props.selectedVehicle);
    }, [props.vehicles, props.selectedVehicle]);

    return (
        <div className={contactDealerCSS}>
            <div className={css.greeting}>
                <h4 className={nameClasses}>
                    {props.identityVerified && custInfo ? custInfo!.firstName + ',' : strings.WelcomeBack}</h4>
                <h3>{strings.WhichDept}</h3>
            </div>
            <hr className={css.hr} />
            <div className={css.tileBox}>
                {specInfo?.svcSpecs ? <DepartmentTile
                    setDepartmentSpecs={setDepartmentSpecs}
                    label={tileLabels.service}
                    sectionHeader={header}
                    theme={props.theme}
                /> : ''}

                {specInfo?.bdySpecs ? <DepartmentTile
                    setDepartmentSpecs={setDepartmentSpecs}
                    label={tileLabels.body}
                    sectionHeader={header}
                    theme={props.theme}
                /> : ''}

                {specInfo?.ossSpecs ? <DepartmentTile
                    setDepartmentSpecs={setDepartmentSpecs}
                    label={tileLabels.oss}
                    sectionHeader={header}
                    theme={props.theme}
                /> : ''}
            </div>
            <hr className={css.hr}/>
            <h1 className={contactDeptHeader} id='displayHeader'>
                {header}
            </h1>
            <div className={sectionArea}>
                <ScrollBar scrollVertical>
                    {sections}
                </ScrollBar>
            </div>
        </div>
    );
};

export default ContactDealer;
